import type { LibraryTemplate } from "../library";
import { ellipse, logo, photo, pill, rect, stack, star, t, text } from "./shorthands";

/** Stories (9:16). */
const FIRST_STORIES: LibraryTemplate[] = [
  {
    id: "story-save-the-date",
    title: "Save the Date",
    description: "Rundes Foto, großes Datum und die wichtigsten Infos",
    presetId: "story",
    topic: "event",
    style: "warm",
    pages: [
      {
        background: "primary",
        nodes: [
          ellipse({ x: -260, y: 1500, w: 560, h: 560, fill: "accent/70", name: "Kreis" }),
          ellipse({ x: 820, y: -120, w: 380, h: 380, fill: "on:primary/12", name: "Kreis" }),
          text({
            text: "SAVE THE DATE",
            style: "subline",
            size: 40,
            color: "on:primary",
            x: 90,
            y: 170,
            w: 900,
            align: "center",
          }),
          photo({ x: 215, y: 270, w: 650, h: 650, ellipse: true, fill: "on:primary/15" }),
          stack({
            x: 90,
            y: 1010,
            w: 900,
            gap: [8, 30],
            align: "center",
            items: [
              t("24.10.", "heading-1", 210, "on:primary"),
              t("Tag der offenen Tür", "heading-2", 76, "on:primary"),
              t("10 bis 16 Uhr · Werkstraße 5 · Eintritt frei", "body", 38, "on:primary/85"),
            ],
          }),
          logo({ x: 340, y: 1700, w: 400, h: 100, on: "primary", align: "center" }),
        ],
      },
    ],
  },
  {
    id: "story-neuer-beitrag",
    title: "Neuer Beitrag",
    description: "Karte mit Vorschaubild – macht auf einen neuen Post aufmerksam",
    presetId: "story",
    topic: "ankuendigung",
    style: "klar",
    pages: [
      {
        background: "surface",
        nodes: [
          logo({ x: 340, y: 150, w: 400, h: 100, on: "surface", align: "center" }),
          rect({
            x: 90,
            y: 330,
            w: 900,
            h: 1080,
            fill: "background",
            radius: "lg",
            shadow: { y: 24, blur: 60, color: "#0000001f" },
            name: "Karte",
          }),
          photo({ x: 130, y: 370, w: 820, h: 640, radius: "md" }),
          stack({
            x: 150,
            y: 1060,
            w: 780,
            gap: 16,
            items: [
              t("NEUER BEITRAG", "subline", 30, "primary"),
              t("5 Fehler, die du bei Flyern vermeiden solltest", "heading-2", 64, "text"),
            ],
          }),
          ...pill(240, 1530, 600, 120, "Jetzt ansehen", "primary", 40),
          text({
            text: "Link in der Bio",
            style: "caption",
            size: 30,
            color: "text-muted",
            x: 240,
            y: 1685,
            w: 600,
            align: "center",
          }),
        ],
      },
    ],
  },
  {
    id: "story-angebot",
    title: "Angebot der Woche",
    description: "Foto oben, Rabatt-Stern und klare Aussage unten",
    presetId: "story",
    topic: "angebot",
    style: "laut",
    pages: [
      {
        background: "secondary",
        nodes: [
          photo({ x: 0, y: 0, w: 1080, h: 1100 }),
          rect({ x: 0, y: 1100, w: 1080, h: 16, fill: "accent", name: "Linie" }),
          rect({
            x: 60,
            y: 80,
            w: 360,
            h: 120,
            fill: "secondary",
            radius: "md",
            name: "Logo-Fläche",
          }),
          logo({ x: 90, y: 100, w: 300, h: 80, on: "secondary" }),
          star({
            x: 700,
            y: 940,
            w: 320,
            h: 320,
            sides: 12,
            innerRatio: 0.78,
            rotation: -8,
            fill: "accent",
            name: "Stern",
          }),
          text({
            text: "-30\u00a0%",
            style: "heading-1",
            size: 92,
            color: "on:accent",
            x: 750,
            y: 990,
            w: 220,
            h: 220,
            rotation: -8,
            align: "center",
          }),
          stack({
            x: 90,
            y: 1230,
            w: 900,
            gap: [16, 28],
            items: [
              t("ANGEBOT DER WOCHE", "subline", 36, "accent"),
              t("Alle Sneaker reduziert", "heading-1", 150, "on:secondary"),
              t("Nur bis Sonntag – solange der Vorrat reicht.", "body", 36, "on:secondary/80"),
            ],
          }),
        ],
      },
    ],
  },
  {
    id: "story-tipp",
    title: "Tipp des Tages",
    description: "Ein Tipp in großer Schrift mit Erklärung und Foto",
    presetId: "story",
    topic: "wissen",
    style: "natur",
    pages: [
      {
        background: "background",
        nodes: [
          ...pill(90, 200, 460, 80, "TIPP DES TAGES", "primary", 32),
          stack({
            x: 90,
            y: 340,
            w: 900,
            gap: 40,
            items: [
              t("Gieß deine Pflanzen lieber morgens als abends.", "heading-1", 92, "text"),
              t(
                "Tagsüber verdunstet das Wasser langsamer im Boden, und die Blätter trocknen schnell ab – so haben Pilze keine Chance.",
                "body",
                40,
                "text-muted",
              ),
            ],
          }),
          photo({ x: 90, y: 1100, w: 900, h: 560, radius: "lg" }),
          logo({ x: 90, y: 1740, w: 300, h: 90, on: "background" }),
          text({
            text: "@deinaccount",
            style: "caption",
            size: 30,
            color: "text-muted",
            x: 590,
            y: 1768,
            w: 400,
            align: "right",
          }),
        ],
      },
    ],
  },
];

/** Dashed frame where an Instagram sticker (poll, countdown, link) goes. */
function stickerSpot(y: number, label: string, on: string) {
  return [
    rect({
      x: 190,
      y,
      w: 700,
      h: 220,
      radius: "lg",
      stroke: { color: `${on}/70`, width: 4, dash: "dashed" },
      name: "Sticker-Platz",
    }),
    text({
      text: label,
      style: "caption",
      size: 30,
      color: `${on}/80`,
      x: 220,
      y,
      w: 640,
      h: 220,
      align: "center",
      name: "Sticker-Hinweis",
    }),
  ];
}

const MORE_STORIES: LibraryTemplate[] = [
  {
    id: "story-umfrage",
    title: "Story: Umfrage",
    description: "Frage mit zwei Fotos zur Wahl und Platz für den Umfrage-Sticker",
    presetId: "story",
    topic: "community",
    style: "verspielt",
    tags: ["umfrage", "abstimmung", "poll", "frage", "sticker", "meinung", "entweder oder"],
    pages: [
      {
        background: "primary",
        nodes: [
          ellipse({ x: 760, y: -140, w: 460, h: 460, fill: "accent/60", name: "Kreis" }),
          ...pill(90, 230, 300, 72, "UMFRAGE", "background", 30),
          text({
            text: "Welche Sorte kommt neu ins Sortiment?",
            style: "heading-1",
            size: 96,
            color: "on:primary",
            x: 90,
            y: 340,
            w: 900,
            h: 340,
          }),
          photo({ x: 90, y: 720, w: 440, h: 540, radius: "lg", fill: "on:primary/15" }),
          photo({ x: 550, y: 720, w: 440, h: 540, radius: "lg", fill: "on:primary/15" }),
          ...pill(120, 1180, 380, 60, "A · Pistazie", "background", 26),
          ...pill(580, 1180, 380, 60, "B · Salted Caramel", "background", 26),
          ...stickerSpot(1320, "Umfrage-Sticker hier platzieren", "on:primary"),
          logo({ x: 340, y: 1650, w: 400, h: 100, on: "primary", align: "center" }),
        ],
      },
    ],
  },
  {
    id: "story-countdown",
    title: "Story: Countdown",
    description: "„Noch 3 Tage“ in großer Schrift, Foto und Platz für den Countdown-Sticker",
    presetId: "story",
    topic: "event",
    style: "nacht",
    tags: ["countdown", "launch", "erinnerung", "sticker", "noch tage", "vorfreude", "start"],
    pages: [
      {
        background: "background",
        nodes: [
          ellipse({ x: -200, y: -200, w: 620, h: 620, fill: "primary/20", name: "Kreis" }),
          logo({ x: 90, y: 200, w: 300, h: 80, on: "background" }),
          stack({
            x: 90,
            y: 340,
            w: 900,
            gap: 16,
            items: [
              t("NOCH", "subline", 44, "accent"),
              t("3 Tage", "heading-1", 240, "primary"),
              t("bis zur Eröffnung unseres neuen Ladens", "heading-2", 60, "text"),
            ],
          }),
          photo({ x: 90, y: 900, w: 900, h: 480, radius: "lg" }),
          ...stickerSpot(1430, "Countdown-Sticker hier platzieren", "text"),
        ],
      },
    ],
  },
];

export const STORIES: LibraryTemplate[] = [...FIRST_STORIES, ...MORE_STORIES];
